import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Log } from "../../../utils";
import { stringifyToJson } from "../../io/Serialization";
import { GltfMesh, GltfTypes } from "./GltfMesh";
/** @internal */
var GltfMeshExport = /** @class */ (function () {
    function GltfMeshExport(pMesh) {
        this.mMesh = pMesh;
        this.mUse32BitIndices = false;
        ;
        this.mPositionsAcc = -1;
        this.mNormalsAcc = -1;
        this.mTexCoordsAcc = -1;
        this.mIndicesAcc = -1;
        this.mPositionsByteLength = 0;
        this.mNormalsByteLength = 0;
        this.mTexCoordsByteLength = 0;
        this.mIndicesByteLength = 0;
        this.mMeshByteLength = 0;
    }
    GltfMeshExport.prototype.exportMesh = function () {
        if (!this.prepareMeshData())
            return (null);
        this.mGltf = {};
        this.mGltf.asset = {};
        this.mGltf.asset.version = "2.0";
        this.mGltf.asset.generator = "W-CF";
        this.createBuffersAndViews();
        this.createAccessors();
        this.createMeshes();
        var tJson = this.getGltfJson();
        if (tJson == null)
            return (null);
        return (this.writeBinaryGltf(tJson));
    };
    GltfMeshExport.prototype.stringToUint8Array = function (pString) {
        var tLength = pString.length;
        var tArray = new Uint8Array(new ArrayBuffer(tLength));
        for (var i = 0; i < tLength; i++)
            tArray[i] = pString.charCodeAt(i);
        return (tArray);
    };
    GltfMeshExport.prototype.negateZComponent = function (pArray) {
        if (pArray == null)
            return (null);
        var tArray = Float32Array.from(pArray);
        for (var j = 0; j < tArray.length; j += 3)
            tArray[j + 2] = -tArray[j + 2];
        return (tArray);
    };
    GltfMeshExport.prototype.prepareMeshData = function () {
        this.mPositions = this.negateZComponent(this.mMesh.getVerticesData(VertexBuffer.PositionKind, false));
        if (this.mPositions == null || this.mPositions.length < 3)
            return (false);
        this.mPositionsByteLength = this.mPositions.length * 4; //float
        this.mUse32BitIndices = (this.mPositions.length / 3) > 65535; //number of vertices
        this.mNormals = this.negateZComponent(this.mMesh.getVerticesData(VertexBuffer.NormalKind, false));
        this.mNormalsByteLength = this.mNormals == null ? 0 : this.mNormals.length * 4; //float
        this.mTexCoords = this.mMesh.getVerticesData(VertexBuffer.UVKind, false);
        this.mTexCoordsByteLength = this.mTexCoords == null ? 0 : this.mTexCoords.length * 4; //float
        this.mIndices = this.mMesh.getIndices(false);
        this.mIndicesByteLength = this.mIndices == null ? 0 : this.mIndices.length;
        if (this.mUse32BitIndices)
            this.mIndicesByteLength *= 4; //unsigned int
        else
            this.mIndicesByteLength *= 2; //unsigned short
        this.mMeshByteLength = this.mPositionsByteLength + this.mNormalsByteLength +
            this.mTexCoordsByteLength + this.mIndicesByteLength;
        return (true);
    };
    GltfMeshExport.prototype.createBuffersAndViews = function () {
        //buffers
        var tBuffer = {};
        tBuffer.byteLength = this.mMeshByteLength;
        this.mGltf.buffers = [tBuffer];
        //bufferViews
        var tView = {};
        tView.buffer = 0;
        tView.byteOffset = 0;
        tView.byteLength = this.mMeshByteLength;
        this.mGltf.bufferViews = [tView];
    };
    GltfMeshExport.prototype.createAccessors = function () {
        var tOffset = 0;
        this.mGltf.accessors = new Array();
        //positions
        if (this.mPositions != null) {
            var tPosAcc = {};
            tPosAcc.bufferView = 0;
            tPosAcc.byteOffset = tOffset;
            tPosAcc.componentType = GltfTypes.ComponentType.FLOAT;
            tPosAcc.type = "VEC3";
            tPosAcc.count = this.mPositions.length / 3;
            this.mGltf.accessors.push(tPosAcc);
            this.mPositionsAcc = this.mGltf.accessors.length - 1;
            tOffset += this.mPositionsByteLength;
        }
        //normals
        if (this.mNormals != null) {
            var tAcc = {};
            tAcc.bufferView = 0;
            tAcc.byteOffset = tOffset;
            tAcc.componentType = GltfTypes.ComponentType.FLOAT;
            tAcc.type = "VEC3";
            tAcc.count = this.mNormals.length / 3;
            this.mGltf.accessors.push(tAcc);
            this.mNormalsAcc = this.mGltf.accessors.length - 1;
            tOffset += this.mNormalsByteLength;
        }
        //tex coords
        if (this.mTexCoords != null) {
            var tAcc = {};
            tAcc.bufferView = 0;
            tAcc.byteOffset = tOffset;
            tAcc.componentType = GltfTypes.ComponentType.FLOAT;
            tAcc.type = "VEC2";
            tAcc.count = this.mTexCoords.length / 2;
            this.mGltf.accessors.push(tAcc);
            this.mTexCoordsAcc = this.mGltf.accessors.length - 1;
            tOffset += this.mTexCoordsByteLength;
        }
        //indices
        if (this.mIndices != null) {
            var tAcc = {};
            tAcc.bufferView = 0;
            tAcc.byteOffset = tOffset;
            tAcc.componentType = this.mUse32BitIndices ?
                GltfTypes.ComponentType.UNSIGNED_INT : GltfTypes.ComponentType.UNSIGNED_SHORT;
            tAcc.type = "SCALAR";
            tAcc.count = this.mIndices.length;
            this.mGltf.accessors.push(tAcc);
            this.mIndicesAcc = this.mGltf.accessors.length - 1;
            tOffset += this.mIndicesByteLength;
        }
    };
    GltfMeshExport.prototype.createMeshes = function () {
        var tPrimitive = {};
        tPrimitive.attributes = {};
        tPrimitive.attributes["POSITION"] = this.mPositionsAcc;
        if (this.mNormals != null)
            tPrimitive.attributes["NORMAL"] = this.mNormalsAcc;
        if (this.mTexCoords != null)
            tPrimitive.attributes["TEXCOORD_0"] = this.mTexCoordsAcc;
        if (this.mIndices != null)
            tPrimitive.indices = this.mIndicesAcc;
        var tMesh = {};
        tMesh.primitives = [tPrimitive];
        this.mGltf.meshes = [tMesh];
    };
    GltfMeshExport.prototype.getGltfJson = function () {
        try {
            return (stringifyToJson(this.mGltf));
        }
        catch (pError) {
            Log.error("Failed to create gltf json: ", pError);
        }
        return (null);
    };
    GltfMeshExport.prototype.writeBinaryGltf = function (pContentJson) {
        try {
            //align content to 4 bytes
            var tContentJson = pContentJson;
            var tContentPadding = 4 - (tContentJson.length % 4);
            if (tContentPadding !== 4) {
                for (var i = 0; i < tContentPadding; ++i)
                    tContentJson += " ";
            }
            var tHeaderLength = 12;
            var tContentArray = this.stringToUint8Array(tContentJson);
            var tContentLength = tContentArray.byteLength;
            var tTotalLength = tHeaderLength;
            tTotalLength += tContentLength + 8; //(8 = type + length)
            tTotalLength += this.mMeshByteLength + 8; //(8 = type + length)
            var tBuffer = new ArrayBuffer(tTotalLength);
            //header
            var tHeader = new Uint32Array(tBuffer, 0, 5);
            tHeader[0] = GltfMesh._GLTF_HEADER_MAGIC;
            tHeader[1] = 2; //version
            tHeader[2] = tTotalLength;
            var tByteOffset = tHeaderLength;
            //start first chunk (json content)
            tHeader[3] = tContentLength;
            tHeader[4] = 0x4E4F534A; //format JSON
            tByteOffset += 8;
            //content
            var tContent = new Uint8Array(tBuffer, tByteOffset, tContentArray.length);
            tContent.set(tContentArray);
            tByteOffset += tContentLength;
            //start second chunk (binary)
            var tBinChunk = new Uint32Array(tBuffer, tByteOffset, 2);
            tBinChunk[0] = this.mMeshByteLength;
            tBinChunk[1] = 0x004E4942; //format BIN
            tByteOffset += 8;
            //positions
            if (this.mPositions != null) {
                var tPositions = new Float32Array(tBuffer, tByteOffset, this.mPositions.length);
                tPositions.set(this.mPositions);
                tByteOffset += this.mPositionsByteLength;
            }
            //normals
            if (this.mNormals != null) {
                var tNormals = new Float32Array(tBuffer, tByteOffset, this.mNormals.length);
                tNormals.set(this.mNormals);
                tByteOffset += this.mNormalsByteLength;
            }
            //tex coords
            if (this.mTexCoords != null) {
                var tTexCoords = new Float32Array(tBuffer, tByteOffset, this.mTexCoords.length);
                tTexCoords.set(this.mTexCoords);
                tByteOffset += this.mTexCoordsByteLength;
            }
            //indices
            if (this.mIndices != null) {
                var tIndices = this.mUse32BitIndices ? new Uint32Array(tBuffer, tByteOffset, this.mIndices.length) :
                    new Uint16Array(tBuffer, tByteOffset, this.mIndices.length);
                tIndices.set(this.mIndices);
                tByteOffset += this.mIndicesByteLength;
            }
            return (tBuffer);
        }
        catch (pError) {
            Log.error("Failed to write gltf arraybuffer: ", pError);
        }
        return (null);
    };
    return GltfMeshExport;
}());
export { GltfMeshExport };
